import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
//services
import changePassword from 'services/forgotPassword/changePassword'
import sendForgotEmailService from 'services/forgotPassword/sendEmail'
import { setError } from 'ducks/user'

// Components
import ForgotForm from '../../components/Forms/Forgot'
import Logo from '../../components/UI/Logo'
import ChangePasswordForm from 'views/components/Forms/changePassword'
import { useNotification } from 'lib/context/notification.context'
import { useTranslation } from 'react-i18next'
import { getActionCodeContext, redeemPasswordRecoveryLink } from 'services/auth/action-code'
import { Container as RecoveryCard, LoginDescription } from './styled'

const PASSWORD_RECOVERY_TECHNICAL_ERROR_MESSAGE =
  'No fue posible procesar la solicitud en este momento. Inténtalo nuevamente más tarde o comunícate con el administrador del sistema.'

const RecoveryContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 28px;
`

const RecoveryTitle = styled.h1`
  margin: 0;
  color: #183449;
  font-family: 'Public Sans', sans-serif;
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.2;
  text-align: center;
`

const RecoveryDescription = styled(LoginDescription)`
  margin: 0;
`

const RecoveryModeLink = styled.button`
  align-self: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #2d749c;
  font-family: 'Public Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    color: #c6016b;
  }
`

const ForgotFormContainer: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { getWarning, getSuccess, getError } = useNotification()
  const navigate = useNavigate()
  const paramValue = localStorage.getItem('i18nextLng')

  const [emailSent, setEmailSent] = useState<boolean>(false)
  const [recoveryEmail, setRecoveryEmail] = useState<string>('')
  const [isLinkValidated, setLinkValidated] = useState<boolean>(false)
  const [isValidatingLink, setValidatingLink] = useState<boolean>(false)
  const [linkError, setLinkError] = useState<string>('')
  const [isSendingEmail, setSendingEmail] = useState<boolean>(false)
  const [isChangingPassword, setChangingPassword] = useState<boolean>(false)
  const [changePasswordSuccess, setChangePasswordSuccess] = useState<boolean>(false)

  useEffect(() => {
    const recoveryToken = new URLSearchParams(
      window.location.hash.replace(/^#/, ''),
    ).get('recovery')

    if (recoveryToken) {
      window.history.replaceState(
        null,
        document.title,
        `${window.location.pathname}${window.location.search}`,
      )
      setValidatingLink(true)
      void redeemPasswordRecoveryLink(recoveryToken)
        .then(() => setLinkValidated(true))
        .catch(() => setLinkError(t('global.common.forgotPassword.invalidLink')))
        .finally(() => setValidatingLink(false))
      return
    }

    void getActionCodeContext('password_recovery')
      .then(() => setLinkValidated(true))
      .catch(() => undefined)
    // El enlace se canjea únicamente al abrir esta ruta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSendEmailForgot = (values: any): void => {
    if (isSendingEmail) return
    setSendingEmail(true)
    const email = values.email?.trim().toLowerCase()
    sendForgotEmailService(email, paramValue || 'es')
      .then(() => {
        setRecoveryEmail(email)
        setEmailSent(true)
        setLinkError('')
        dispatch(setError(false))
      })
      .catch((error) => {
        const status = error?.response?.status

        if (status === 400) {
          getError(t('global.notifications.emailNotExist'))
          return
        }

        if (status === 422) {
          getWarning(t('global.notifications.emailNotMain'))
          return
        }

        if (status === 421) {
          getError(t('global.notifications.userNotActive'))
          return
        }

        getError(PASSWORD_RECOVERY_TECHNICAL_ERROR_MESSAGE)
      })
      .finally(() => setSendingEmail(false))
  }

  const handleChangePassword = (pass: string): void => {
    if (isChangingPassword) return
    setChangingPassword(true)
    changePassword({
      password: pass,
    })
      .then(() => {
        getSuccess(t('global.common.forgotPassword.notifications.success'))
        setChangePasswordSuccess(true)
        setTimeout(() => {
          navigate('/')
        }, 3000)
      })
      .catch(() => {
        getWarning(t('global.common.forgotPassword.notifications.error'))
      })
      .finally(() => setChangingPassword(false))
  }

  return (
    <RecoveryCard>
      <Logo width="245px" />
      <RecoveryContent>
        {isValidatingLink ? (
          <RecoveryTitle>{t('global.common.forgotPassword.validatingLink')}</RecoveryTitle>
        ) : isLinkValidated ? (
          <>
            {!changePasswordSuccess ? (
              <ChangePasswordForm onSubmit={(e) => handleChangePassword(e.password)} isProcessing={isChangingPassword} />
            ) : (
              <>
                <RecoveryTitle>{t('global.common.forgotPassword.changedPassword')}</RecoveryTitle>
                <RecoveryDescription>
                  {t('global.common.forgotPassword.changedPasswordDescription')}
                </RecoveryDescription>
              </>
            )}
          </>
        ) : emailSent ? (
          <>
            <RecoveryTitle>{t('global.common.forgotPassword.emailSended')}</RecoveryTitle>
            <RecoveryDescription>
              {t('global.common.forgotPassword.emailSendedDescription', { email: recoveryEmail })}
            </RecoveryDescription>
            <RecoveryModeLink onClick={() => setEmailSent(false)} type="button">
              {t('global.common.forgotPassword.requestAnotherLink')}
            </RecoveryModeLink>
          </>
        ) : (
          <>
            <RecoveryTitle>{t('global.common.forgotPassword.title')}</RecoveryTitle>
            <RecoveryDescription>
              {linkError || t('global.common.forgotPassword.description')}
            </RecoveryDescription>
            <ForgotForm onSubmit={(values) => handleSendEmailForgot(values)} isProcessing={isSendingEmail} />
          </>
        )}
      </RecoveryContent>
    </RecoveryCard>
  )
}

export default ForgotFormContainer
